// ============================================================
//  src/context/AuthContext.js
// ============================================================
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { loginUser as apiLogin, logoutUser as apiLogout, getDashboard } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(() => localStorage.getItem('sAuthToken'));
  const [loading, setLoading] = useState(true);

  const saveToken = useCallback((t) => {
    if (t) localStorage.setItem('sAuthToken', t);
    else   localStorage.removeItem('sAuthToken');
    setToken(t);
  }, []);

  // ── Bootstrap: rehydrate user from /dashboard on page load ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = localStorage.getItem('sAuthToken');
      if (!stored) { setLoading(false); return; }

      // Restore user from localStorage immediately so UI doesn't
      // flash a logout while the dashboard call is in flight
      const cached = localStorage.getItem('authUser');
      if (cached) {
        try { setUser(JSON.parse(cached)); } catch { /* ignore bad JSON */ }
      }

      try {
        const { data } = await getDashboard();
        if (!cancelled && data?.success) {
          const u = data.user ?? data.data?.user ?? null;
          setUser(u);
          if (u) localStorage.setItem('authUser', JSON.stringify(u));
        } else if (!cancelled) {
          // Server explicitly returned success:false — clear session
          saveToken(null);
          setUser(null);
          localStorage.removeItem('authUser');
        }
      } catch (err) {
        if (!cancelled) {
          // Only log out on 401 — keep session alive for network errors,
          // timeouts, 500s, CORS issues etc.
          if (err?.response?.status === 401) {
            saveToken(null);
            setUser(null);
            localStorage.removeItem('authUser');
          }
          // Any other error: leave token intact, user stays logged in
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── login ─────────────────────────────────────────────────
  const login = useCallback(async (identifier, password) => {
    const { data } = await apiLogin({ identifier, password });
    if (!data?.success) throw new Error(data?.message || 'Login failed.');
    // login returns { success, message, data: { token, user } }
    const token = data.data?.token ?? data.token;
    const user  = data.data?.user  ?? data.user;
    if (!token) throw new Error('No token received from server.');
    saveToken(token);
    setUser(user);
    localStorage.setItem('authUser', JSON.stringify(user));
    return user;
  }, [saveToken]);

  // ── logout ────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try { await apiLogout(); } catch { /* ignore */ }
    saveToken(null);
    setUser(null);
    localStorage.removeItem('authUser');
  }, [saveToken]);

  // ── refreshUser: merge partial update into user state ─────
  const refreshUser = useCallback((partial) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...(partial || {}) };
      localStorage.setItem('authUser', JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);